import { EventEmitter, inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { BehaviorSubject, firstValueFrom, timer } from "rxjs";
import { map } from "rxjs/operators";
import {
  CollectionFilters,
  CollectionMultiSelectComponent,
} from "src/app/common/components/collections-multi-select/collections-multi-select.component";
import { DragAndDropItem } from "src/app/common/models/drag-and-drop-item.model";
import { NotificationService } from "src/app/common/services/notification.service";
import { PillarboxService } from "src/app/common/services/pillarbox.service";
import { HomePageService } from "src/app/modules/home-page/services/home-page.service";
import { Speech2TextType } from "src/app/modules/speech2text/models/speech2text.model";

@Injectable({
  providedIn: "root",
})
export class FloatingAssistantChatService {
  setMedia = new EventEmitter<DragAndDropItem>();
  collectionSelectRef: DynamicDialogRef | undefined;

  openAssistantForHelpEmitter = new EventEmitter<void>();
  openAssistantWithScrapedContentAndSummaryEmitter = new EventEmitter<{
    scrapedContent: string;
    summary: string;
  }>();
  newChatCreated = new EventEmitter<string>();
  openAssistantWithChatIdEvent = new EventEmitter<string>();
  openAssistantForRagEmitter = new EventEmitter<CollectionFilters>();

  clickOnLink = new EventEmitter<number>();
  setPredefinedPromptByLabel = new EventEmitter<string>();
  private readonly dialogService = inject(DialogService);
  protected readonly pillarboxService = inject(PillarboxService);
  protected readonly homePageService = inject(HomePageService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);

  private readonly speech2TextTypeSubject = new BehaviorSubject<Speech2TextType>("AUDIO");
  speech2TextTypeObservable = this.speech2TextTypeSubject.asObservable();
  private readonly context: { speech2TextType?: Speech2TextType } = {};

  visible = false;
  buttonsVisibility = true;

  draggedItem: DragAndDropItem | null = null;
  selectedMediaItems: DragAndDropItem[] = [];
  selectionMode = false;

  addMedia(media?: DragAndDropItem): void {
    if (media) {
      this.draggedItem = media;
    }
    if (this.draggedItem) {
      if (this.selectedMediaItems.length >= 10) {
        this.notificationService.showWarning(
          $localize`:@@floating_assistant_chat_service_too_many_media_warning:Vous ne pouvez pas sélectionner plus de 10 médias`,
        );
        return;
      }
      if (this.selectedMediaItems.some((a) => a.id === this.draggedItem?.id)) {
        this.notificationService.showWarning(
          $localize`:@@floating_assistant_chat_service_media_already_selected_warning:Média déjà sélectionné`,
        );
        return;
      }
      this.selectedMediaItems = [...this.selectedMediaItems, this.draggedItem];
      this.draggedItem = null;
    }
  }

  removeMedia(id: string): void {
    this.selectedMediaItems = this.selectedMediaItems.filter((item) => item.id !== id);
  }

  openAssistantWithContent(media: DragAndDropItem): void {
    this.visible = true;
    this.buttonsVisibility = true;
    this.setMedia.emit(media);
    this.addMedia(media);
  }

  openAssistantWithChatId(chatId: string): void {
    this.visible = true;
    this.buttonsVisibility = true;
    this.openAssistantWithChatIdEvent.emit(chatId);
  }

  openAssistantWithScrapedContentAndSummary(
    scrapedContent: string,
    summary: string,
    predefinedPromptLabel?: string,
  ): void {
    this.visible = true;
    this.buttonsVisibility = true;
    this.openAssistantWithScrapedContentAndSummaryEmitter.emit({
      scrapedContent,
      summary,
    });
    if (predefinedPromptLabel) {
      this.setPredefinedPromptByLabel.emit(predefinedPromptLabel);
    }
  }

  async updateSpeech2TextType(newType: Speech2TextType): Promise<void> {
    const currentType = await firstValueFrom(this.speech2TextTypeObservable); // Async check of current value
    if (newType !== currentType) {
      this.speech2TextTypeSubject.next(newType);
    }
  }

  toggleVisibility(): void {
    this.visible = !this.visible;
    this.buttonsVisibility = true;
  }

  setVisibility(isVisible: boolean): void {
    this.visible = isVisible;
    this.buttonsVisibility = true;
  }

  openAssistantForHelp(): void {
    this.visible = true;
    this.buttonsVisibility = false;
    this.openAssistantForHelpEmitter.emit();
  }

  determineMaxTemperature(model: string): number {
    if (model.startsWith("gemini-")) {
      return 1.0;
    } else if (model.startsWith("gpt-")) {
      return 2.0;
    }
    return 2.0; // Default max temperature for other models
  }

  async onLinkClickWithVideo(videoId: number, time?: number): Promise<void> {
    const features = await this.homePageService.getFeatures();
    if (features[videoId] === undefined) {
      console.error("Video not found in features");
      return;
    }
    this.pillarboxService.openDialogWithVideo(features[videoId], time);
  }

  async onLinkClick(link: HTMLAnchorElement): Promise<void> {
    try {
      const linkInnerHtml = link.innerHTML;

      if (linkInnerHtml.includes("video")) {
        await this.handleVideoLinks(linkInnerHtml);
        return;
      }

      const linkHref = link.getAttribute("href");
      if (linkHref) {
        await this.handleHrefBasedLinks(link);
      }

      if (/\b\d{1,2}:\d{2}\b/.test(linkInnerHtml)) {
        await this.handleTimePatternLinks(linkInnerHtml);
      }
    } catch (error) {
      console.error("Error during onLinkClick execution:", error);
    }
  }

  private async handleVideoLinks(linkInnerHtml: string): Promise<void> {
    const regex = /video(\d+)/g;
    const match = regex.exec(linkInnerHtml);

    if (!match?.[1]) {
      console.error("Invalid video link");
      return;
    }
    const videoId = parseInt(match[1], 10);
    const timeString = linkInnerHtml.split(" - ")[1];
    const timeInSeconds = timeString ? this.timeStringToSeconds(timeString) : undefined;
    await this.onLinkClickWithVideo(videoId, timeInSeconds);
  }

  private async sleep(ms: number): Promise<void> {
    return void (await firstValueFrom(timer(ms).pipe(map(() => void 0)))); // Fixed toPromise deprecation
  }

  private async waitForPlayerToBeReady(): Promise<void> {
    const maxRetries = 20;
    const retryInterval = 500;
    let retries = 0;

    while (!this.pillarboxService.hasPlayer()) {
      if (retries >= maxRetries) {
        throw new Error("Player did not initialize within the expected time");
      }
      await this.sleep(retryInterval);
      retries++;
    }
  }

  private async handleHrefBasedLinks(link: HTMLAnchorElement): Promise<void> {
    try {
      const linkHref = link.getAttribute("href")!;
      const timecode = link.dataset["timecode"];

      if (linkHref) {
        const speech2TextType = await firstValueFrom(this.speech2TextTypeObservable);
        if (speech2TextType) {
          const newHref = this.generateSpeech2TextLink(linkHref, speech2TextType);

          if (newHref) {
            await this.router.navigateByUrl("/", { skipLocationChange: true });
            await this.router.navigateByUrl(newHref);

            await this.waitForPlayerToBeReady();

            const timeInSeconds = timecode ? parseFloat(timecode) : 0;
            this.pillarboxService.currentTime = timeInSeconds; // Set playback time
            await this.pillarboxService.play();
          } else {
            console.error("Could not generate speech2text link");
          }
        }
      }
    } catch (error) {
      console.error("Error during handleHrefBasedLinks execution:", error);
    }
  }

  private async handleTimePatternLinks(linkInnerHtml: string): Promise<void> {
    const timeInSeconds = this.timeStringToSeconds(linkInnerHtml);
    this.pillarboxService.currentTime = timeInSeconds;
    await this.pillarboxService.play();
    this.clickOnLink.emit(timeInSeconds);
  }

  private generateSpeech2TextLink(linkHref: string, speech2TextType: string): string {
    let newHref = "";

    // Case 1: linkHref is numeric
    if (/^\d+$/.test(linkHref)) {
      newHref = speech2TextType === "AUDIO" ? `/radio/radio-detail/${linkHref}` : `/play-rts/video-detail/${linkHref}`;
    }
    // Case 2: linkHref contains "whatson:" or "via-logfusion:"
    else if (linkHref.includes("whatson:") || linkHref.includes("via-logfusion:")) {
      newHref = speech2TextType === "AUDIO" ? `/radio/radio-detail/${linkHref}` : `/play-rts/video-detail/${linkHref}`;
    }

    return newHref;
  }

  private timeStringToSeconds(timeString: string): number {
    const parts = timeString.split(":").map((part) => parseInt(part, 10));
    let seconds = 0;
    if (parts.length === 3) {
      seconds = parts[0]! * 3600 + parts[1]! * 60 + parts[2]!;
    } else if (parts.length === 2) {
      seconds = parts[0]! * 60 + parts[1]!;
    } else {
      throw new Error("Invalid time format");
    }
    return seconds;
  }

  openDialogToSelectCollections(): void {
    this.collectionSelectRef = this.dialogService.open(CollectionMultiSelectComponent, {
      header: $localize`:@@chat_input_collection_tooltip:Ajouter des émissions à rechercher (Expérimental)`,
    });
    this.collectionSelectRef.onClose.subscribe((collectionFilters: CollectionFilters) => {
      if (collectionFilters) {
        this.visible = true;
        this.openAssistantForRagEmitter.emit(collectionFilters);
        this.setPredefinedPromptByLabel.emit("Recherche dans collections");
      }
    });
  }
}
